import React, { useEffect, useState } from 'react';
import { App, Button, Input, Segmented, Switch } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import dynamicFormsAdminService from '../../services/dynamicFormsAdminService';
import { truthy } from './fieldTypes';

const URL_RE = /^https?:\/\/\S+$/i;

/**
 * Whether the form is open, the payment step after submitting, and its language.
 * Saves with the same calls as the old Form Settings window.
 */
const SettingsPanel = ({ formId, formName, meta, onMetaChange, language, onLanguageChange, languageBusy, liveUrl }) => {
  const { message } = App.useApp();
  const savedActive = truthy(meta?.is_active);
  const savedPaymentOn = truthy(meta?.payment_enabled);
  const savedLink = meta?.payment_link || '';
  const [active, setActive] = useState(savedActive);
  const [paymentOn, setPaymentOn] = useState(savedPaymentOn);
  const [link, setLink] = useState(savedLink);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setActive(savedActive);
    setPaymentOn(savedPaymentOn);
    setLink(savedLink);
    setError(null);
  }, [savedActive, savedPaymentOn, savedLink]);

  const dirty = active !== savedActive || paymentOn !== savedPaymentOn || link.trim() !== savedLink;

  const save = async () => {
    const trimmed = link.trim();
    if (trimmed && !URL_RE.test(trimmed)) {
      setError('Enter a full web address starting with https://');
      return;
    }
    if (paymentOn && !trimmed) {
      setError('Add the payment page address, or turn the payment step off.');
      return;
    }
    try {
      setSaving(true);
      if (trimmed !== savedLink) {
        const response = await dynamicFormsAdminService.updateForm(formId, { payment_link: trimmed || null });
        if (!response.success) throw new Error(response.error || 'Could not save the payment link');
      }
      if (active !== savedActive) {
        const response = await dynamicFormsAdminService.toggleFormActive(formId, active);
        if (!response.success) throw new Error(response.error || 'Could not change whether the form is open');
      }
      if (paymentOn !== savedPaymentOn) {
        const response = await dynamicFormsAdminService.togglePayment(formId, paymentOn);
        if (!response.success) throw new Error(response.error || 'Could not change the payment step');
      }
      message.success('Settings saved');
      await onMetaChange();
    } catch (err) {
      message.error(err.message || 'Could not save the settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dfw-settings">
      <section className="dfw-group" aria-labelledby="dfw-s-status">
        <h3 className="dfw-group__title" id="dfw-s-status">Accepting responses</h3>
        <label className="dfw-toggle dfw-toggle--row">
          <Switch checked={active} onChange={setActive} />
          <span>
            <span className="dfw-toggle__label">{active ? 'Open' : 'Closed'}</span>
            <span className="dfw-toggle__hint">
              {active ? 'Anyone with the link can send the form.' : 'The link shows a “this form has closed” page.'}
            </span>
          </span>
        </label>
      </section>

      <section className="dfw-group" aria-labelledby="dfw-s-pay">
        <h3 className="dfw-group__title" id="dfw-s-pay">Payment after submitting</h3>
        <label className="dfw-toggle dfw-toggle--row">
          <Switch checked={paymentOn} onChange={(v) => { setPaymentOn(v); setError(null); }} />
          <span>
            <span className="dfw-toggle__label">{paymentOn ? 'Send applicants to pay' : 'No payment step'}</span>
            <span className="dfw-toggle__hint">
              {paymentOn ? 'After sending the form, applicants go straight to the payment page.' : 'Applicants see the confirmation message after sending the form.'}
            </span>
          </span>
        </label>
        <div className="dfw-field">
          <label className="dfw-label" htmlFor="dfw-s-link">Payment page</label>
          <Input
            id="dfw-s-link"
            prefix={<LinkOutlined aria-hidden="true" />}
            value={link}
            onChange={(e) => { setLink(e.target.value); setError(null); }}
            placeholder="https://"
            status={error ? 'error' : undefined}
          />
          {error && <p className="dfw-error" role="alert">{error}</p>}
        </div>
      </section>

      <section className="dfw-group" aria-labelledby="dfw-s-lang">
        <h3 className="dfw-group__title" id="dfw-s-lang">Language</h3>
        <div className="dfw-label-row">
          <p className="dfw-hint dfw-hint--flush">
            Sets the application card title and button: “{language?.application_text || 'Application'}” and “{language?.submit_text || 'Submit Application'}”. Changes straight away.
          </p>
          <Segmented
            value={language?.language_id === 2 ? 2 : 1}
            onChange={onLanguageChange}
            options={[{ value: 1, label: 'Maltese' }, { value: 2, label: 'English' }]}
            disabled={languageBusy}
            aria-label="Form language"
          />
        </div>
      </section>

      <section className="dfw-group" aria-labelledby="dfw-s-about">
        <h3 className="dfw-group__title" id="dfw-s-about">About this form</h3>
        <dl className="dfw-answers dfw-answers--compact">
          <div className="dfw-answers__row"><dt>Name</dt><dd>{formName}</dd></div>
          {meta?.form_slug && <div className="dfw-answers__row"><dt>Address</dt><dd><a href={liveUrl} target="_blank" rel="noopener noreferrer">{liveUrl}</a></dd></div>}
          <div className="dfw-answers__row"><dt>Form number</dt><dd className="dfw-num">{formId}</dd></div>
        </dl>
      </section>

      <div className="dfw-savebar">
        <span className="dfw-savebar__state">{dirty ? 'You have changes that are not saved.' : 'All changes saved.'}</span>
        <span className="dfw-grow" />
        {dirty && <Button onClick={() => { setActive(savedActive); setPaymentOn(savedPaymentOn); setLink(savedLink); setError(null); }}>Undo changes</Button>}
        <Button type="primary" onClick={save} loading={saving} disabled={!dirty}>Save settings</Button>
      </div>
    </div>
  );
};

export default SettingsPanel;
